// actions.ts — Endpoints actions globales + statuts + notes + export + import.
import { downloadBlob, req, upload, type ActionMutationOptions } from './_internals.js';

export const actionsApi = {
  // Action statuses
  getActionStatuses: () => req('GET', '/action-statuses'),
  createActionStatus: (d: unknown) => req('POST', '/action-statuses', d),
  updateActionStatus: (id: number | string, d: unknown) => req('PUT', '/action-statuses/' + id, d),
  deleteActionStatus: (id: number | string) => req('DELETE', '/action-statuses/' + id),
  reorderActionStatuses: (order: unknown) => req('PUT', '/action-statuses/reorder', order),

  // Global actions list (split notify : `?notify=false` désactive la
  // notification email — split button "Enregistrer / Sans notifier").
  createAction: (d: unknown, opts: ActionMutationOptions = {}) =>
    req('POST', '/actions' + (opts.notify === false ? '?notify=false' : ''), d),
  // include_completed défaut côté serveur = true (rétro-compatible). On ne
  // passe `include_completed=false` que pour les listes opérationnelles
  // (liste #actions par défaut, picker lier/copier) afin de ne pas transférer
  // les actions terminées/fusionnées.
  // `facets` : le vocabulaire de filtre serveur partagé avec /actions/search et
  // avec les tuiles du Workspace (cf. services/action_facets.py). C'est ce qui
  // permet à une tuile d'atterrir sur EXACTEMENT la population qu'elle a
  // comptée — la page rejoue les facettes que le serveur a publiées, elle ne
  // les reconstruit pas.
  getAllActions: (opts?: {
    includeDrafts?: boolean;
    includeCompleted?: boolean;
    facets?: Record<string, string>;
  }) => {
    const params = new URLSearchParams();
    if (opts?.includeDrafts) params.set('include_drafts', 'true');
    if (opts?.includeCompleted === false) params.set('include_completed', 'false');
    for (const [k, v] of Object.entries(opts?.facets || {})) {
      if (v !== '' && v != null) params.set(k, v);
    }
    const qs = params.toString();
    return req('GET', '/actions' + (qs ? '?' + qs : ''));
  },
  // Recherche « retrouver une action » (palette omnibox). `qs` = querystring déjà
  // encodée (?q=…&person_id=…&mine=true…). Réponse : {groups:[{items}], suggestions}.
  searchActions: (qs = '') => req('GET', '/actions/search' + qs),
  // Lecture d'une seule action (évite de charger toute la liste pour un détail).
  // Renvoie aussi les drafts. 404 si hors périmètre RBAC.
  getAction: (id: number | string) => req('GET', '/actions/' + id),
  updateAction: (id: number | string, d: unknown, opts: ActionMutationOptions = {}) =>
    req('PUT', '/actions/' + id + (opts.notify === false ? '?notify=false' : ''), d),
  markActionSeen: (id: number | string) => req('POST', '/actions/' + id + '/seen'),
  // Liste exhaustive des personnes/équipes qui voient l'action (survol « Qui
  // voit l'action » dans l'éditeur). Calcul à la demande côté serveur.
  getActionViewers: (id: number | string) => req('GET', '/actions/' + id + '/viewers'),

  // Historique des versions (traçabilité). `getActionVersions` = liste méta
  // (version_no + date + auteur) ; `getActionVersion` = snapshot complet figé.
  getActionVersions: (id: number | string) => req('GET', '/actions/' + id + '/versions'),
  getActionVersion: (id: number | string, n: number | string) =>
    req('GET', '/actions/' + id + '/versions/' + n),

  // Brouillons standalone (wizard « Analyser un CR »). Crée l'action avec
  // to_validate=true, puis le wizard publie via publishAction ou supprime
  // via deleteDraftAction. Le serveur refuse delete/publish si l'action
  // n'est plus en brouillon.
  publishAction: (id: number | string) => req('POST', '/actions/' + id + '/publish', {}),
  deleteDraftAction: (id: number | string) => req('DELETE', '/actions/' + id),

  // Notes
  getActionNotes: (id: number | string) => req('GET', '/actions/' + id + '/notes'),
  addActionNote: (id: number | string, d: unknown) => req('POST', '/actions/' + id + '/notes', d),
  updateActionNote: (id: number | string, nid: number | string, d: unknown) =>
    req('PUT', '/actions/' + id + '/notes/' + nid, d),
  deleteActionNote: (id: number | string, nid: number | string) =>
    req('DELETE', '/actions/' + id + '/notes/' + nid),

  // Export binaire (XLSX/JSON via downloadBlob)
  exportActions: (payload: unknown): Promise<Blob> => downloadBlob('/actions/export', payload),

  // Fusion : absorbe `absorbed_id` dans `keeper_id` et applique l'état final
  // choisi dans l'UI (`overrides` = champs explicitement écrasés). Permission
  // serveur : actions.merge. Pas de notification (décision UX mai 2026).
  mergeActions: (payload: { keeper_id: number; absorbed_id: number; overrides?: unknown }) =>
    req('POST', '/actions/merge', payload),

  // Affectation comité en masse : ajoute `committee_id` aux comités de chaque
  // action de `action_ids` et trace le changement par une note. Permission
  // serveur : actions.write.
  bulkAssignCommittee: (payload: { action_ids: number[]; committee_id: number }) =>
    req('POST', '/actions/bulk-assign-committee', payload),

  // Import XLS : envoie un fichier .xlsx (format export), reçoit les actions
  // parsées ({actions, count}). Ne crée rien — la création passe par createAction.
  importActionsXls: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return upload('/actions/import-xls', fd);
  },
};
